import { useCallback, useEffect, useRef } from 'react';

import { TaskScheduler } from '@air/classes-task-scheduler';

import { useEditCanvasStore } from '../store';
import { type EditUploadContentType } from '../types';
import { toDisplayImageUrl } from '../utils/getImageNodeDisplayUrl';
import { isVideoEditUploadContentType } from '../utils/uploadMimeTypes';

/** Maximum concurrent upload+preload operations across all invocations. */
const UPLOAD_CONCURRENCY = 5;

/**
 * Maximum items per getEditUploadUrl request. The endpoint errors above 10.
 */
const URL_BATCH_SIZE = 10;

export type UploadResult = { status: 'fulfilled'; cdnUrl: string } | { status: 'rejected' };

const preloadCdnUrl = (cdnUrl: string, contentType: EditUploadContentType): Promise<void> => {
  if (isVideoEditUploadContentType(contentType)) {
    return new Promise((resolvePreload) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.onloadedmetadata = () => resolvePreload();
      video.onerror = () => resolvePreload();
      video.src = cdnUrl;
    });
  }

  return new Promise((resolvePreload) => {
    const img = new window.Image();
    img.onload = () => resolvePreload();
    img.onerror = () => resolvePreload();
    img.src = toDisplayImageUrl(cdnUrl) ?? cdnUrl;
  });
};

/**
 * Uploads files to S3 via presigned URLs and preloads CDN media into the browser cache.
 * Presigned URLs are fetched in chunks of {@link URL_BATCH_SIZE}. Uploads are queued
 * through a shared {@link TaskScheduler} (held in a ref) so that at most
 * {@link UPLOAD_CONCURRENCY} uploads run at once — even across multiple rapid invocations.
 * Returns per-item results so callers can handle partial failures.
 */
export const useUploadAndPreload = () => {
  // Shared across all uploadAndPreload calls so concurrent invocations
  // (e.g. user drops 10 files, then immediately drops 10 more) compete
  // for the same worker pool instead of doubling concurrency.
  const schedulerRef = useRef<TaskScheduler | null>(null);
  useEffect(() => {
    schedulerRef.current = new TaskScheduler(UPLOAD_CONCURRENCY);
  }, []);

  const uploadAndPreload = useCallback(
    async (items: Array<{ file: File; contentType: EditUploadContentType }>): Promise<UploadResult[]> => {
      const getEditUploadUrl = useEditCanvasStore.getState().getEditUploadUrl;

      if (!getEditUploadUrl) {
        throw new Error('getEditUploadUrl not configured');
      }

      // Fetch presigned URLs in chunks — the endpoint errors above URL_BATCH_SIZE.
      // This is a lightweight metadata call so we don't route it through the
      // scheduler; only the heavier S3 uploads need concurrency control.
      const uploadInfos: Array<{ presignedUrl: string; cdnUrl: string }> = [];

      for (let start = 0; start < items.length; start += URL_BATCH_SIZE) {
        const batch = items.slice(start, start + URL_BATCH_SIZE);
        const { files } = await getEditUploadUrl(batch.map((item) => ({ contentType: item.contentType })));
        uploadInfos.push(...files);
      }

      // Each upload task is wrapped in a Promise whose resolve/reject are
      // captured inside run() via .then(resolve).catch(reject). This bridges
      // the scheduler's callback-driven model with Promise.allSettled below.
      // (Same pattern as NodePreviewManager.getPreviewForNode.)
      // Task IDs use crypto.randomUUID() to avoid collisions when multiple
      // uploadAndPreload calls overlap on the shared scheduler.
      const itemPromises = items.map((item, i) => {
        const uploadInfo = uploadInfos[i];

        return new Promise<string>((resolve, reject) => {
          schedulerRef.current?.addTasks([
            {
              id: crypto.randomUUID(),
              run: () => {
                if (!uploadInfo) {
                  reject(new Error('No upload URL received'));
                  // Return a resolved promise so the scheduler treats the
                  // task as finished (not errored), since we already
                  // captured the failure via reject() above.
                  return Promise.resolve();
                }

                return fetch(uploadInfo.presignedUrl, {
                  body: item.file,
                  headers: { 'Content-Type': item.contentType },
                  method: 'PUT',
                })
                  .then((response) => {
                    if (!response.ok) {
                      throw new Error('Upload failed');
                    }

                    return preloadCdnUrl(uploadInfo.cdnUrl, item.contentType);
                  })
                  .then(() => resolve(uploadInfo.cdnUrl))
                  .catch(reject);
              },
            },
          ]);
        });
      });

      const settled = await Promise.allSettled(itemPromises);

      return settled.map(
        (r): UploadResult =>
          r.status === 'fulfilled' ? { cdnUrl: r.value, status: 'fulfilled' } : { status: 'rejected' },
      );
    },
    [],
  );

  return { uploadAndPreload };
};
